/**
 * RouteSurfacePolicy centralizes the "same mounted surface" rules shared by
 * TTRouter and TTWindowManager.
 *
 * A surface is the mounted TTViewLoader/component tree for one module/action,
 * for example Employee edit, TimeSheet list, Schedule list, or a report view.
 * The generic edit shell is the one exception after it is already mounted:
 * new/view/edit are live modes inside one EditRecordsTabbed instance so actions
 * such as View -> Edit and Save & Continue do not discard field, tab, sizing, or
 * record-navigation state.
 *
 * Repro this protects: Employee -> Titles -> View a row -> click Edit in the
 * editor shell context menu. The URL action changes from `a=view` to `a=edit`,
 * but TTWindowManager should keep the same EditRecordsTabbed component alive.
 * If `a` remains part of the Vue render key for that mounted edit shell, Vue
 * destroys and recreates the shell, the lane-size bootstrap class returns, and
 * users see a brief blank/sizing flash even though the same record and same TTS
 * edit definition are still active.
 *
 * The policy therefore distinguishes "mode inside this mounted shell" from
 * "different surface". The mutable/read-only mode (`a=new`, `a=view`, `a=edit`)
 * can change in place once the edit shell exists. Definition selectors such as
 * `edit_name`, `list_name`, `shell_name`, and `wizard_name` must remain structural because
 * those values choose different field sets, tabs, context-menu actions, and
 * component bodies. Treating those as URL state would trade the flash for stale
 * or mismatched UI, which is a worse failure mode.
 * Some URL params are live state owned by that mounted surface, so changing
 * them should update route data and watchers without destroying the DOM. Other
 * URL params are structural: they select a different TTS list/edit definition,
 * wizard, report shape, routed filter set, or window placement. Structural
 * params must stay in the comparison/render key so the view can rebuild instead
 * of showing stale tabs, fields, buttons, or data.
 */
class RouteSurfacePolicy {
	/**
	 * Params that never define a different component surface.
	 *
	 * `t` is the freshness/history timestamp added by TTRouter. `tab` is active
	 * tab state inside an already-selected list/edit/report definition; the tab
	 * body should react through component state, not by remounting the window.
	 */
	static global_url_state_params = Object.freeze( ['t', 'tab'] );

	/**
	 * Default URL-state params by route action.
	 *
	 * Keep this list intentionally short. Params such as `edit_name`,
	 * `list_name`, `shell_name`, `wizard_name`, `filter_data`, flags (`f`), and arbitrary
	 * search params are structural by default because TTViewLoader uses them to
	 * choose schemas, tab sets, context menus, filters, and wizard components.
	 */
	static action_url_state_params = Object.freeze( {
		/*
		 * `new` has no stable record id yet, so the only safe built-in URL state
		 * here is the action itself. Save & Continue updates a mounted `new`
		 * editor into `edit` after the API returns the saved id; remounting during
		 * that promotion loses tab/field focus and reopens the same edit-shell
		 * sizing path that produces the View -> Edit flash.
		 */
		new: Object.freeze( ['a'] ),

		/*
		 * `edit` treats both the action and record id as state owned by the
		 * mounted EditRecordsTabbed shell. The action has to be live state because
		 * View -> Edit only changes mutability and context-menu enablement for the
		 * same TTS edit definition. The id has to stay live state because the edit
		 * record navigator intentionally swaps the form data in place while
		 * preserving shell width, tabs, and record-navigation controls.
		 */
		edit: Object.freeze( ['a', 'id'] ),

		/*
		 * `view` mirrors edit because read-only and editable instances are the
		 * same shell with a different mode. Keeping this aligned with `edit`
		 * prevents asymmetric behavior where Edit -> View preserves the shell but
		 * View -> Edit remounts it, or where record navigation is stable in one
		 * mode but flashes in the other.
		 */
		view: Object.freeze( ['a', 'id'] ),
		report: Object.freeze( ['id'] ), // Saved-report navigation is record identity inside one report module.
		shell: Object.freeze( [] ), // shell_name is structural; owner payload remains route-stack state rather than mutable URL state.
		wizard: Object.freeze( ['step', 'current_step'] ), // Step state is safe only when `wizard_name` still matches.
	} );

	/**
	 * View-owned URL-state declarations read from `route_data.data.view_stack`.
	 *
	 * `render_key_ignored_params` already existed for TimeSheet/Schedule render
	 * stability. It now also participates in same-surface preservation because a
	 * param that should not remount the render key should not force the router to
	 * bump `route_revision` either. New code should prefer `url_state_params`;
	 * the other names are accepted to keep existing route data working.
	 */
	static view_stack_url_state_param_keys = Object.freeze( [
		'url_state_params',
		'same_surface_ignored_params',
		'render_key_ignored_params',
	] );

	/**
	 * Parse a TimeTrex hash/path/query fragment into URLSearchParams.
	 */
	static getUrlParameters( path ) {
		let normalized_path = typeof path === 'string' ? path : '';

		if ( normalized_path.startsWith( '#/' ) ) {
			normalized_path = normalized_path.slice( 2 );
		} else if ( normalized_path.startsWith( '#' ) || normalized_path.startsWith( '/' ) ) {
			normalized_path = normalized_path.slice( 1 );
		}

		return new URLSearchParams( normalized_path );
	}

	/**
	 * Return the action from a path when callers do not already have it.
	 */
	static getActionFromPath( path, fallback_action = 'list' ) {
		return this.getUrlParameters( path ).get( 'a' ) || fallback_action || 'list';
	}

	/**
	 * Keep param lists sanitized before using them in route key comparisons.
	 */
	static normalizeParamList( ...param_lists ) {
		const normalized_params = new Set();

		param_lists.forEach( param_list => {
			if ( Array.isArray( param_list ) !== true ) {
				return;
			}

			param_list.forEach( param => {
				if ( typeof param === 'string' && param.length > 0 ) {
					normalized_params.add( param );
				}
			} );
		} );

		return [...normalized_params];
	}

	/**
	 * Extract the view_stack payload from TTRouteData without assuming every
	 * caller has a fully initialized route-data object.
	 */
	static getViewStackPayload( route_data ) {
		const route_payload = route_data?.getData?.() || {};
		const view_stack = route_payload.view_stack;

		return view_stack && typeof view_stack === 'object' && Array.isArray( view_stack ) !== true
			? view_stack
			: {};
	}

	/**
	 * Return view-declared URL-state params from route data.
	 */
	static getDeclaredUrlStateParams( route_data ) {
		const view_stack = this.getViewStackPayload( route_data );
		const param_lists = this.view_stack_url_state_param_keys.map( key => view_stack[key] );

		return this.normalizeParamList( ...param_lists ).filter( param => param !== 't' );
	}

	/**
	 * Merge owner-declared URL state into the shared route-data shape.
	 *
	 * Owners supply only their domain parameter names; keeping the payload merge
	 * here prevents TimeSheet and Schedule from duplicating router metadata rules.
	 */
	static declareUrlStateParams( route_data, url_state_params = [] ) {
		if ( typeof route_data?.setData !== 'function' ) {
			return false;
		}

		const route_payload = route_data.getData?.() || {};
		const view_stack = this.getViewStackPayload( route_data );

		route_data.setData( {
			...route_payload,
			view_stack: {
				...view_stack,
				url_state_params: this.normalizeParamList( view_stack.url_state_params, url_state_params ),
			},
		} );

		return true;
	}

	/**
	 * Capture only URL state declared by the mounted surface.
	 *
	 * Declared state uses patch semantics: an omitted target value preserves the
	 * mounted owner's current selection.
	 */
	static getDeclaredUrlStateSnapshot( path_or_params, route_data ) {
		const params = path_or_params instanceof URLSearchParams
			? new URLSearchParams( path_or_params )
			: this.getUrlParameters( path_or_params );
		const declared_params = this.getDeclaredUrlStateParams( route_data );
		const snapshot = {};

		declared_params.forEach( param => {
			const values = [
				...params.getAll( param ),
				...params.getAll( param + '[]' ),
			];

			if ( values.length === 1 ) {
				snapshot[param] = values[0];
			} else if ( values.length > 1 ) {
				snapshot[param] = values;
			}
		} );

		return snapshot;
	}

	/**
	 * Compare a partial target snapshot using the same omission-preserves-current
	 * semantics used by hidden same-surface navigation.
	 */
	static hasUrlStatePatchChanged( current_state = {}, target_state = {} ) {
		const normalized_current_state = current_state && typeof current_state === 'object' && Array.isArray( current_state ) !== true
			? current_state
			: {};
		const normalized_target_state = target_state && typeof target_state === 'object' && Array.isArray( target_state ) !== true
			? target_state
			: {};

		return JSON.stringify( normalized_current_state ) !== JSON.stringify( {
			...normalized_current_state,
			...normalized_target_state,
		} );
	}

	/**
	 * Compare semantic declared URL state for one preserved surface.
	 */
	static hasDeclaredUrlStateChanged( current_path, next_path, route_data ) {
		return this.hasUrlStatePatchChanged(
			this.getDeclaredUrlStateSnapshot( current_path, route_data ),
			this.getDeclaredUrlStateSnapshot( next_path, route_data )
		);
	}

	/**
	 * Return the complete URL-state param list for one action.
	 *
	 * This intentionally combines defaults with view-declared params. Defaults
	 * cover stable framework-level behavior; view declarations cover special
	 * screens such as TimeSheet/Schedule date, employee, mode, and settings
	 * selectors without teaching the router about those domain-specific names.
	 */
	static getUrlStateParams( action, route_data = null, extra_params = [] ) {
		const normalized_action = action || 'list';

		return this.normalizeParamList(
			this.global_url_state_params,
			this.action_url_state_params[normalized_action] || [],
			this.getDeclaredUrlStateParams( route_data ),
			extra_params
		);
	}

	/**
	 * Return URL-state params for comparing two route entries.
	 *
	 * Include declarations from both sides so a newly-created TTRouteData object
	 * can opt into the same URL-state behavior as the current mounted view.
	 */
	static getUrlStateParamsForRoutePair( action, current_route_data = null, next_route_data = null, extra_params = [] ) {
		return this.normalizeParamList(
			this.getUrlStateParams( action, current_route_data ),
			this.getDeclaredUrlStateParams( next_route_data ),
			extra_params
		);
	}

	/**
	 * Delete known URL-state params from a URLSearchParams copy.
	 */
	static removeUrlStateParams( params, url_state_params ) {
		url_state_params.forEach( param => {
			params.delete( param );
			params.delete( param + '[]' );
		} );

		return params;
	}

	/**
	 * Sort different parameter names so harmless query ordering differences do not
	 * force a rebuild, while preserving occurrence order for duplicate names and
	 * their [] array spelling. URL consumers can use last-value, latest-shape, or
	 * ordered-array semantics for those entries.
	 */
	static getStableParamString( params ) {
		const entries = [...params.entries()].sort( ( first, second ) => {
			const first_name = first[0].endsWith( '[]' ) ? first[0].slice( 0, -2 ) : first[0];
			const second_name = second[0].endsWith( '[]' ) ? second[0].slice( 0, -2 ) : second[0];

			if ( first_name === second_name ) {
				return 0;
			}

			return first_name.localeCompare( second_name );
		} );

		return new URLSearchParams( entries ).toString();
	}

	/**
	 * Return the structural route key after URL-state params are removed.
	 */
	static getStructuralPathKey( path, url_state_params ) {
		const params = this.getUrlParameters( path );

		this.removeUrlStateParams( params, url_state_params );

		return this.getStableParamString( params );
	}

	/**
	 * Normalize the path segment used in TTWindowManager's Vue component key.
	 *
	 * If this key changes, Vue destroys/recreates the routed component tree. It
	 * should therefore include only structural route params plus route_revision,
	 * leaving live URL-state params for the mounted component to handle.
	 */
	static getWindowRenderPathKey( path, route_data = null, extra_params = [] ) {
		const action = this.getActionFromPath( path, route_data?.getAction?.() || 'list' );
		const url_state_params = this.getUrlStateParams( action, route_data, extra_params );

		return this.getStructuralPathKey( path, url_state_params );
	}

	/**
	 * Decide whether a same module/action route can update the mounted DOM.
	 *
	 * The router already matched module/action before calling this. This method
	 * compares the remaining structural params after removing URL-state params.
	 * A `true` result means route data/watchers should update the mounted surface;
	 * a `false` result means TTRouter must bump `route_revision` and let Vue
	 * recreate the component tree for a structurally different surface.
	 */
	static shouldPreserveSameSurfaceDOM( {
		current_path = '',
		next_path = '',
		action = null,
		current_route_data = null,
		next_route_data = null,
		extra_params = [],
	} = {} ) {
		const resolved_action = action || next_route_data?.getAction?.() || current_route_data?.getAction?.() || this.getActionFromPath( next_path );
		const url_state_params = this.getUrlStateParamsForRoutePair( resolved_action, current_route_data, next_route_data, extra_params );
		const current_structural_key = this.getStructuralPathKey( current_path, url_state_params );
		const next_structural_key = this.getStructuralPathKey( next_path, url_state_params );

		return current_structural_key === next_structural_key;
	}
}

export { RouteSurfacePolicy };
